import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  GetAgreementImportContextRequest,
  GetAgreementImportContextResponse,
  ParseHistoricalCsvRequest,
  ParseHistoricalCsvResponse,
  GetProductGroupsForMappingRequest,
  GetProductGroupsForMappingResponse,
  ImportHistoricalTransactionsRequest,
  ImportHistoricalTransactionsResponse,
  SuggestProductMappingsRequest,
  SuggestProductMappingsResponse,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/**
 * Hook to get agreement context for historical import
 * Returns agreement, supplier, company, and rexel_code info
 */
export function useGetAgreementImportContext(
  input: GetAgreementImportContextRequest,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.rebates.historicalImport.getAgreementContext.queryOptions({
      input,
    }),
    enabled,
  });

  return {
    ...query,
    data: query.data as GetAgreementImportContextResponse | undefined,
  };
}

/**
 * Hook to parse CSV and extract product codes
 * Filters by rexel code and year, extracts unique product codes
 */
export function useParseCsv() {
  const { showToast } = useToast();

  return useMutation<
    ParseHistoricalCsvResponse,
    Error,
    ParseHistoricalCsvRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.rebates.historicalImport.parseCsv(input);
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to parse CSV',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

/**
 * Hook to get product groups for a supplier (for mapping)
 * Returns product groups with their existing mapped codes
 */
export function useGetProductGroupsForMapping(
  input: GetProductGroupsForMappingRequest,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.rebates.historicalImport.getProductGroupsForMapping.queryOptions({
      input,
    }),
    enabled,
  });

  return {
    ...query,
    productGroups: (
      query.data as GetProductGroupsForMappingResponse | undefined
    )?.productGroups,
  };
}

/**
 * Hook to import historical transactions
 * Creates invoices, line items, associations, and resets forecast
 */
export function useImportHistoricalTransactions() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    ImportHistoricalTransactionsResponse,
    Error,
    ImportHistoricalTransactionsRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.rebates.historicalImport.importTransactions(input);
    },
    onSuccess: (data) => {
      showToast({
        title: 'Import Complete',
        message: `Created ${data.invoicesCreated} invoices with ${data.lineItemsCreated} line items. Total value: $${data.totalValue.toLocaleString()}`,
        color: 'success',
        icon: 'check',
      });

      // Invalidate relevant queries
      void queryClient.invalidateQueries({
        queryKey: ['rebate-agreements'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['rebate-invoices'],
      });
    },
    onError: (error) => {
      showToast({
        title: 'Import Failed',
        message: error.message || 'Failed to import historical transactions',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

/**
 * Hook to get AI-powered product mapping suggestions
 * Uses AI to suggest product group mappings based on product names
 */
export function useSuggestProductMappings() {
  const { showToast } = useToast();

  return useMutation<
    SuggestProductMappingsResponse,
    Error,
    SuggestProductMappingsRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.rebates.historicalImport.suggestMappings(input);
    },
    onError: (error) => {
      showToast({
        title: 'AI Suggestion Failed',
        message: error.message || 'Failed to get AI suggestions for mappings',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
